import { useMemo, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import useUserStatus from "../../../../../../hooks/user/useUserStatus";
import useEvent from "../../../../../../hooks/event-hooks/useEvent";
import { setActiveTabAndMaxTabLimit } from "../../../../../../app/store/slices/event/event";
import { setEventSettingActiveTab } from "../../../../../../app/store/slices/event/settings/eventSettings";
import { AUTO_CANCEL_EVENT_HOURS } from "../../../../../../app/store/constant";

export default function useSetupProgress() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { eventData } = useSelector((state) => state.event);

    const userStatus = useUserStatus();
    const eventStatus = useEvent();

    const [expandSetupProgress, setExpandSetupProgress] = useState(false);
    const [expandEventDetails, setExpandEventDetails] = useState(false);
    const [expandBankDetails, setExpandBankDetails] = useState(false);
    const [expandTaxInvoice, setExpandTaxInvoice] = useState(false);
    const [progress, setProgress] = useState(0);

    const [locationTooltip, setLocationTooltip] = useState('')

    useEffect(() => {
        if (!eventData?.is_publishable?.location || eventData?.is_publishable?.location === 0) {
            if (eventData?.start_date && eventData?.start_time && eventData?.end_date && eventData?.end_time) {

                const eventStartDateAndTime = dayjs(
                    `${eventData?.start_date} ${eventData?.start_time}`,
                    'YYYY-MM-DD HH:mm'
                );

                const currentDateTime = dayjs();

                // Calculate the difference in hours
                const diffInHours = eventStartDateAndTime.diff(currentDateTime, 'hour');

                // console.log("Difference in Hours for Location Tooltip -> ", diffInHours);

                if (diffInHours > AUTO_CANCEL_EVENT_HOURS) {
                    setLocationTooltip(`You can publish event without location`);
                } else if (diffInHours < AUTO_CANCEL_EVENT_HOURS && diffInHours > 24) {
                    setLocationTooltip(`Fill the location details to avoid auto-cancellation`);
                } else if (diffInHours <= 24) {
                    setLocationTooltip(`Location details are mandatory`);
                } else {
                    setLocationTooltip(``);
                }
            }
        }
    }, [eventData?.start_date, eventData?.start_time]);

    // console.log("Location Tooltip -> ", locationTooltip);


    function handleSetupProgressExpand() {
        setExpandSetupProgress(!expandSetupProgress);
    }

    function handleEventDetailsExpand() {
        setExpandEventDetails(!expandEventDetails);
    }

    function handleBankDetailsExpand() {
        setExpandBankDetails(!expandBankDetails);
    }

    function handleTaxInvoiceExpand() {
        setExpandTaxInvoice(!expandTaxInvoice);
    }


    /**
     * @function computeMaxTabLimit
     * @description Computes the maximum tab limit based on the clicked index and the completion status of event details.
     * @param {number} clickedIndex - The index of the tab that was clicked (1-based).
     * @returns {number} - The maximum tab limit that can be accessed.
     * Steps:
     * 1. Store the is_publishable object from eventData.
     * 2. Define the order of steps in an array.
     * 3. Initialize highestCompleted with clickedIndex.
     * 4. Loop through the order of steps:
     *   a. If the step is marked as complete (1), update highestCompleted to the maximum of itself and the current index + 1.
     * 5. Return highestCompleted as the maximum tab limit.
     */
    function computeMaxTabLimit(clickedIndex) {
        const publishable = eventData?.is_publishable || {};

        const order = ['details', 'schedule', 'description', 'media', 'location'];

        let highestCompleted = clickedIndex;

        for (let i = 0; i < order.length; i++) {
            if (publishable[order[i]] === 1) {
                highestCompleted = Math.max(highestCompleted, i + 1);
            }
        }

        return highestCompleted;
    }


    /**
     * @function computeConfigMax
     * @description Computes the maximum tab limit for the config button based on the clicked index and the completion status of event details.
     * @param {number} clickedIndex - The index of the tab that was clicked (1-based).
     * Steps:
     * 1. Store the is_publishable object from eventData.
     * 2. Define the order of steps in an array.
     * 3. Get the key of the clicked step.
     * 4. If the clicked step is incomplete, return the clicked index.
     * 5. Loop through the steps after the clicked index to find the first incomplete step.
     * 6. If found, return its index + 1.
     * 7. If all further steps are complete, return the total number of steps.
     * @returns {number} - The maximum tab limit that can be accessed via the config button.
     */
    function computeConfigMax(clickedIndex) {
        const publishable = eventData?.is_publishable || {};
        const order = ['details', 'schedule', 'description', 'media', 'location'];
        const total = order.length;

        const clickedKey = order[clickedIndex - 1];

        if (publishable[clickedKey] !== 1) {
            return clickedIndex;
        }

        for (let i = clickedIndex; i < total; i++) {
            if (publishable[order[i]] !== 1) {
                return i + 1;
            }
        }

        return total;
    }


    /**
     * @function setTab
     * @description Sets the active tab and maximum tab limit based on the mode ('edit' or 'config') and navigates to the event details page.
     * @param {number} activeTab - The index of the tab to be set as active (1-based).
     * @param {string} mode - The mode of operation, either 'edit' or 'config'.
     * Steps:
     * 1. Determine the maxTabLimit based on the mode:
     *   a. If mode is 'config', call computeConfigMax with activeTab.
     *   b. If mode is 'edit', call computeMaxTabLimit with activeTab.
     * 2. Dispatch the setActiveTabAndMaxTabLimit action with the activeTab and computed maxTabLimit.
     * 3. Navigate to the event details page using the event slug.
     */
    function setTab(activeTab, mode /* 'edit' | 'config' */) {
        const maxTabLimit = mode === 'config'
            ? computeConfigMax(activeTab)
            : computeMaxTabLimit(activeTab);

        dispatch(setActiveTabAndMaxTabLimit({ activeTab, maxTabLimit }));
        navigate(`/event/${eventData?.slug}/event-details`);
    }


    const tasks = useMemo(() => [
        {
            id: 1,
            title: 'Add Event Details',
            completed: eventStatus.allEventDetailsCompleted,
            isExpandable: true,
            subTasks: [
                {
                    id: 'basic-details',
                    title: 'Basic Details',
                    completed: eventStatus.isBasicDetailsCompleted,
                    disableBtn: false,
                    tooltip: '',
                    onEdit: () => setTab(1, 'edit'),
                    onConfig: () => setTab(1, 'config')
                },
                {
                    id: 'schedule',
                    title: 'Schedule',
                    completed: eventStatus.isScheduleCompleted && eventStatus.isBasicDetailsCompleted,
                    disableBtn: !eventStatus.isBasicDetailsCompleted,
                    tooltip: !eventStatus.isBasicDetailsCompleted
                        ? 'Complete the Basic Details step to configure Schedule.'
                        : '',
                    onEdit: () => setTab(2, 'edit'),
                    onConfig: () => setTab(2, 'config')
                },
                {
                    id: 'description',
                    title: 'Description',
                    completed: eventStatus.isDescriptionCompleted && eventStatus.isScheduleCompleted,
                    disableBtn: !eventStatus.isScheduleCompleted,
                    tooltip: !eventStatus.isScheduleCompleted
                        ? 'Complete the Schedule step to configure Description.'
                        : '',
                    onEdit: () => setTab(3, 'edit'),
                    onConfig: () => setTab(3, 'config')
                },
                {
                    id: 'media',
                    title: 'Media',
                    completed: eventStatus.isMediaCompleted && eventStatus.isDescriptionCompleted,
                    disableBtn: !eventStatus.isDescriptionCompleted,
                    tooltip: !eventStatus.isDescriptionCompleted
                        ? 'Complete the Description step to configure Media.'
                        : '',
                    onEdit: () => setTab(4, 'edit'),
                    onConfig: () => setTab(4, 'config')
                },
                {
                    id: 'location',
                    title: 'Location',
                    completed: eventStatus.isLocationCompleted && eventStatus.isMediaCompleted,
                    disableBtn: !eventStatus.isMediaCompleted,
                    tooltip: !eventStatus.isMediaCompleted
                        ? 'Complete the Media step to configure Location.'
                        : locationTooltip,
                    onEdit: () => setTab(5, 'edit'),
                    onConfig: () => setTab(5, 'config')
                }
            ]
        },
        {
            id: 2,
            title: 'Create Tickets',
            completed: eventStatus.isTicketCreated,
            disableBtn: !eventStatus.isScheduleCompleted,
            tooltip: !eventStatus.isScheduleCompleted ? 'Complete the Schedule step to create tickets.' : '',
            isExpandable: false,
            onEdit: () => {
                navigate(`/event/${eventData?.slug}/tickets`);
            },
            onConfig: () => {
                navigate(`/event/${eventData?.slug}/tickets`);
            }
        },
        ...(((eventStatus?.isBankDetailsAddedForEvent !== null || eventStatus?.isCommissionPaidByEndUser !== null) && eventStatus.isPaidTicketCreated) ? [{
            id: 3,
            title: 'Set Up Bank Details',
            completed: eventStatus.isBankDetailsAddedForEvent === 1 &&
                eventStatus.isCommissionPaidByEndUser === 1,
            isExpandable: true,
            subTasks: [
                {
                    id: 'add-bank-account',
                    title: 'Add Bank Account',
                    completed: eventStatus.isBankDetailsAddedForEvent === 1,
                    disableBtn: false,
                    tooltip: '',
                    onEdit: () => {
                        dispatch(setEventSettingActiveTab('bankDetails'));
                        navigate(`/event/${eventData?.slug}/settings`);
                    },
                    onConfig: () => {
                        dispatch(setEventSettingActiveTab('bankDetails'));
                        navigate(`/event/${eventData?.slug}/settings`);
                    }
                },
                {
                    id: 'setup-commission-details',
                    title: 'Set Up Commission Details',
                    completed: eventStatus.isCommissionPaidByEndUser === 1,
                    disableBtn: false,
                    tooltip: '',
                    onEdit: () => {
                        dispatch(setEventSettingActiveTab('commission'));
                        navigate(`/event/${eventData?.slug}/settings`);
                    },
                    onConfig: () => {
                        dispatch(setEventSettingActiveTab('commission'));
                        navigate(`/event/${eventData?.slug}/settings`);
                    }
                }
            ],
        }] : []),
        ...(((eventStatus?.hasTaxInvoiceDetailsProvided !== null || eventStatus?.isSACCodeAdded !== null) && eventStatus.isPaidTicketCreated) ? [{
            id: 4,
            title: 'Tax & Invoice Settings',
            completed: eventStatus.hasTaxInvoiceDetailsProvided === 1 && eventStatus.isSACCodeAdded === 1,
            isExpandable: true,
            subTasks: [
                {
                    id: 'tax-invoice-setup',
                    title: 'Tax & Invoice Setup',
                    completed: eventStatus.hasTaxInvoiceDetailsProvided === 1,
                    disableBtn: false,
                    tooltip: '',
                    onEdit: () => {
                        dispatch(setEventSettingActiveTab('taxInvoice'));
                        navigate(`/event/${eventData?.slug}/settings`);
                    },
                    onConfig: () => {
                        navigate(`/event/${eventData?.slug}/settings`);
                    }
                },
                {
                    id: 'add-sac-code',
                    title: 'Add SAC Code',
                    completed: eventStatus.isSACCodeAdded === 1,
                    disableBtn: false,
                    tooltip: '',
                    onEdit: () => {
                        dispatch(setEventSettingActiveTab('taxInvoice'));
                        navigate(`/event/${eventData?.slug}/settings`);
                    },
                    onConfig: () => {
                        navigate(`/event/${eventData?.slug}/settings`);
                    }
                }
            ],
        }] : []),

    ], [eventStatus, userStatus, locationTooltip, eventData, navigate, setTab])


    /**
     * @description Calculate the overall progress percentage based on completed tasks and subtasks.
     * Steps:
     * 1. If there are no tasks, set progress to 0 and return.
     * 2. Initialize total and completed counters.
     * 3. Loop through each task:
     *  a. If the task has subtasks, count only the subtasks:
     *    i. Add the number of subtasks to total.
     *   ii. Add the number of completed subtasks to completed.
     * b. If the task has no subtasks, count the task itself:
     *   i. Increment total by 1.
     *  ii. Increment completed by 1 if the task is completed.
     * 4. Calculate the percentage of completed tasks.
     * 5. Update the progress state with the calculated percentage.
     * @dependency tasks
     */
    useEffect(() => {
        if (!tasks || tasks.length === 0) {
            setProgress(0);
            return;
        }

        let total = 0;
        let completed = 0;

        tasks.forEach(task => {
            if (Array.isArray(task.subTasks) && task.subTasks.length > 0) {
                // Count only subTasks, ignore parent 'completed'
                total += task.subTasks.length;
                completed += task.subTasks.filter(st => st.completed).length;
            } else {
                // Standalone task
                total += 1;
                completed += task.completed ? 1 : 0;
            }
        });

        const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
        setProgress(pct);
    }, [tasks]);

    // console.log("Tasks -> ", tasks);


    return {
        expandSetupProgress,
        expandEventDetails,
        handleSetupProgressExpand,
        handleEventDetailsExpand,
        tasks,
        progress,
        expandBankDetails,
        handleBankDetailsExpand,
        expandTaxInvoice,
        handleTaxInvoiceExpand
    }
}

/**
 * 1. details
 * 2. schedule
 * 3. description
 * 4. media
 * 5. location
 * 6. tickets
 * 7. bankAttached
 * 8. commissionPaidBy
 * 9. taxInvoiceDetails
 * 10. sacCode
 */

/**
 * 1. By default load all the task in ui until any ticket created
 * 2. Once the ticket created then look for a keys of last 4 task and check their value
 *    2.1 If value is === 0 then do not load the 0th value tasks
 *    2.2 If value is === 1 then load the 1th value tasks
 * 3. Mark the completed value by checking the value of last 4 option. if one then mark else do not mark
 */

/**
 * Functionality of Edit and Config buttons:
 * 1. Disable the config button if any of the previous tasks are not completed.
 * 2. Only enable config button if previous tasks are completed.
 */